import { Component, Host, h, Prop, Listen, Element, Method, State } from '@stencil/core';
import PhotoSwipe, { PhotoSwipeOptions } from 'photoswipe';
import PhotoSwipeLightbox from 'photoswipe/dist/photoswipe-lightbox.esm.js';
import { DOMElement } from '../../entities';

export type Lightbox = PhotoSwipeLightbox;
export type PhotoSwipeType = PhotoSwipe;

@Component({
  tag: 'me-lightbox',
  styleUrl: 'me-lightbox.scss',
  shadow: false,
})
export class MeLightbox {

  @Element() host: HTMLMeLightboxElement;

  @Prop() options?: PhotoSwipeOptions;

  @State() private initCommands: ReadonlyMap<string, (lightbox: Lightbox) => void> = new Map;

  private lightbox?: Lightbox;

  @Listen('click', { target: 'window' })
  async handleWindowClick(event: PointerEvent) {
    if (!(event.target instanceof DOMElement)) {
      return;
    }

    const toggle = event.target.closest(`[data-me-lightbox-toggle="${this.host.id}"]`);

    if (!(toggle instanceof HTMLElement)) {
      return;
    }

    await this.open(Number(toggle.dataset.meLightboxIndex));
  }

  componentDidLoad() {
    this.initLightbox();
  }

  componentDidUpdate() {
    this.initLightbox();
  }

  render() {
    return <Host/>;
  }

  @Method()
  async open(index: number = 0) {
    this.lightbox.loadAndOpen(index);
  }

  @Method()
  async enableSharing(handler: (pswp: PhotoSwipeType) => void) {
    await this.addInitCommand('share-btn', (lightbox) => {
      lightbox.on('uiRegister', () => {
        const shareIconClass = bowser.is("ios")
          ? "fa-arrow-up-from-bracket"
          : "fa-share-nodes";

        lightbox.pswp.ui.registerElement({
          name: 'share-btn',
          ariaLabel: 'Teilen',
          order: 9,
          isButton: true,
          html: `<i class="me-lightbox__share-icon fa-solid fa-fw ${shareIconClass}"></i>`,
          onInit: (_el, pswp) => {
            pswp.on('change', () => {
              const shareIcon: HTMLElement = document.querySelector('.pswp__button--share-btn');
              shareIcon.style.display = pswp.currSlide.data.html ? 'none' : null;
            });
          },
          onClick: (_event, _el, pswp) => handler(pswp)
        });
      });
    });
  }

  /**
   * Adds an init command that is guaranteed to be executed once on every update
   *
   * This is used to further configure the PhotoSwipe instance.
   * E.g. to register additional elements / event listeners …
   */
  @Method()
  async addInitCommand(key: string, cmd: (lightbox: Lightbox) => void) {
    this.initCommands = new Map([
      ...this.initCommands.entries(),
      [key, cmd],
    ]);
  }

  private initLightbox() {
    this.lightbox?.destroy();

    this.lightbox = new PhotoSwipeLightbox({
      pswpModule: PhotoSwipe,
      ...this.options
    });

    this.initCommands.forEach(cmd => cmd(this.lightbox));

    this.lightbox.init();
  }
}
